import {
  applyNodeChanges,
  type Node,
  type NodeChange,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import { useEffect, useMemo, useState } from 'react';
import { useLatest } from 'react-use';

import { useHelperLines } from '@/components/diagrams/FlowChart/components/HelperLines';
import { useAsyncLayout } from '@/components/diagrams/FlowChart/hooks/useAsyncLayout';
import { usePhaseGesture } from '@/components/diagrams/FlowChart/hooks/usePhaseGesture';
import { cascadePhases } from '@/components/diagrams/FlowChart/utils/phaseLayout';
import { diagnostics } from '@/utils/diagnostics';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import {
  getLayoutKey,
  getTileSizes,
  layoutDiagram,
} from '../utils/diagramLayout';
import {
  DIAGRAM_FIT_VIEW_OPTIONS,
  MIN_STAGE_SPACING,
} from '../utils/diagramLayout.config';
import { getStageGeometry } from '../utils/diagramStages';
import {
  canUseLayoutRoute,
  createReactFlowDiagram,
  type ReactFlowDiagram,
  type ReactFlowDiagramNode,
} from '../utils/reactFlowDiagram';

/**
 * Holds the live React Flow diagram of the builder canvas: the tiles and
 * connections, the drag state, and the automatic layout.
 *
 * `diagram` is the saved document. The React Flow diagram comes from it, and
 * adds the state that is not saved: measured tile sizes, the selection, the
 * tile positions during a drag, and the ELK routes. This hook does not change
 * `diagram`. An edit changes `diagram`, and the React Flow diagram follows it.
 *
 * ELK lays out the diagram after React Flow measures every tile, as in the
 * estate waterfall. It runs again only when the layout key changes, or when
 * the user selects "Auto tiles". A text edit can change the size of a tile,
 * but it does not run ELK. A diagram whose tiles all have saved positions
 * does not need ELK.
 *
 * Routes are valid only for the positions that ELK solved. They do not show
 * while a tile moves, or when saved positions replace the ELK positions. A
 * connection with a set side, route shape, or waypoints never uses them.
 *
 * A tile that the user drags or resizes towards another stage pushes that
 * stage ahead, so the tiles of two stages never mix. The stage goes back when
 * the tile goes back.
 *
 * A dragged or resized tile snaps to the edges and centers of the other
 * tiles. `helperLines` gives the alignment lines to show while it moves.
 *
 * Each tile calls `onDelete` from its Delete button. The shared tile node
 * shows this button only when its node data has an `onDelete` function. The
 * other tile edits need no function in the node data: the tile text modal
 * gets `editNode` from the editing context, the selection bar is outside
 * React Flow, and React Flow reports drags and resizes through
 * `onNodesChange`. `DiagramFlowChart` adds the label actions of the
 * connections and stage bands, because it copies those nodes and edges. It
 * does not copy the tiles, so the tile action goes in here. Give a stable
 * function: a new function creates every tile again.
 */
export function useReactFlowDiagram(
  diagram: DiagramBuilder_DiagramFragment,
  onDelete?: (nodeId: string) => void
) {
  // The tiles and arrows of `diagram`, without the React Flow state.
  const fromDiagram = useMemo((): ReactFlowDiagram => {
    const created = createReactFlowDiagram(diagram);
    return {
      ...created,
      nodes: created.nodes.map((node) => ({
        ...node,
        data: { ...node.data, onDelete },
      })),
    };
  }, [diagram, onDelete]);
  const [nodes, setNodes] = useNodesState(fromDiagram.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(fromDiagram.edges);
  const [dragging, setDragging] = useState(false);
  const trackPhaseGesture = usePhaseGesture<ReactFlowDiagramNode>();
  const isPinned = diagram.content.nodes.every((node) => node.position);
  // The node state updates in an effect after the content changes. Take the
  // tile list and set widths from the content, and measured sizes from the state.
  const tileSizes = getTileSizes(fromDiagram, nodes);
  const latest = useLatest({ diagram, tileSizes });
  const layout = useAsyncLayout(
    isPinned || !tileSizes ? null : getLayoutKey(diagram, fromDiagram),
    // The input is null until every tile has a size, so the sizes exist here.
    () => layoutDiagram(latest.current.diagram, latest.current.tileSizes ?? {}),
    (error) => diagnostics.warn('diagram builder layout failed', { error })
  );
  const { result } = layout;
  const isPlaced = result !== null || isPinned || layout.didFail;

  // Keep the React Flow state of each tile, such as its size, selection, and
  // position, when the diagram content changes. Saved positions come first.
  useEffect(() => {
    setNodes((current) =>
      fromDiagram.nodes.map((node) => {
        const previous = current.find((item) => item.id === node.id);
        return {
          ...node,
          measured: previous?.measured,
          selected: previous?.selected,
          position:
            node.data.item.position ?? previous?.position ?? node.position,
        };
      })
    );
    setEdges((current) =>
      fromDiagram.edges.map((edge) => ({
        ...edge,
        selected: current.find((item) => item.id === edge.id)?.selected,
      }))
    );
  }, [fromDiagram, setNodes, setEdges]);

  // A new layout moves every tile that has no saved position.
  useEffect(() => {
    if (!result) return;
    const placed = new Map(result.nodes.map((node) => [node.id, node]));
    setNodes((current) =>
      current.map((node) => ({
        ...node,
        position: placed.get(node.id)?.position ?? node.position,
      }))
    );
  }, [result, setNodes]);

  const { fitView } = useReactFlow();
  // Fit the view after React Flow renders the first placed tiles. This is the
  // only automatic fit, so a tile that the user adds later stays where they
  // clicked.
  useEffect(() => {
    if (!isPlaced) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        void fitView(DIAGRAM_FIT_VIEW_OPTIONS);
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [isPlaced, fitView]);

  const { helperLineHorizontal, helperLineVertical, getHelperLinesChanges } =
    useHelperLines();

  const onNodesChange = (changes: NodeChange<Node>[]) => {
    // Snap before the stage cascade, so the stages move for the position
    // where the tile settles. The default helper line configuration uses
    // only tiles, so the stage bands do not snap or show lines.
    //
    // React Flow also reports a `dimensions` change when it measures a node
    // whose content changed, such as a new display. The helper lines treat
    // each size change as a resize, so they get only the drags and resizes
    // of the user. An empty change list hides the lines.
    const isGesture = changes.some(
      (change) =>
        (change.type === 'position' && change.dragging) ||
        (change.type === 'dimensions' && change.resizing)
    );
    const gestureChanges = getHelperLinesChanges(
      isGesture ? changes : [],
      nodes
    );
    const snapped = isGesture ? gestureChanges : changes;
    // React Flow also reports changes to the stage bands, which are not
    // tiles. The diagram content decides which tiles exist, so changes that
    // add or replace a node do not apply.
    const tileChanges = snapped.filter(
      (change): change is NodeChange<ReactFlowDiagramNode> =>
        change.type !== 'add' &&
        change.type !== 'replace' &&
        nodes.some((node) => node.id === change.id)
    );
    const gesture = trackPhaseGesture(tileChanges, nodes);
    const resizedIDs = new Set(
      tileChanges.flatMap((change) =>
        change.type === 'dimensions' ? [change.id] : []
      )
    );
    setNodes((current) => {
      // React Flow writes a new size into the `measured` object of the node.
      // A shallow node copy shares that object with the previous node and the
      // gesture baseline, so copy it first. Otherwise the cascade sees the new
      // size in all three, and finds no stage that changed.
      const next = applyNodeChanges(
        tileChanges,
        current.map((node) =>
          resizedIDs.has(node.id)
            ? { ...node, measured: { ...node.measured } }
            : node
        )
      );
      if (!gesture.canMovePhases) return next;
      return cascadePhases({
        nodes: next,
        previousNodes: current,
        baselineNodes: gesture.baselineNodes,
        geometry: getStageGeometry(latest.current.diagram),
        minimumGap: () => MIN_STAGE_SPACING,
      });
    });
  };

  const showRoutes =
    !dragging && !layout.isPending && !layout.didFail && !isPinned;
  // Keep the identity of the edge list until the edges or their routes
  // change, so that the canvas does not render every edge again.
  const routedEdges = useMemo(() => {
    const routes = new Map(result?.edges.map((edge) => [edge.id, edge.data]));
    return edges.map((edge) => {
      const route =
        showRoutes && canUseLayoutRoute(edge) ? routes.get(edge.id) : undefined;
      return {
        ...edge,
        data: {
          ...edge.data!,
          routeWaypoints: route?.routeWaypoints,
          labelPosition: route?.labelPosition,
        },
      };
    });
  }, [edges, result, showRoutes]);

  return {
    nodes,
    edges: routedEdges,
    onNodesChange,
    onEdgesChange,
    /** The alignment lines to show while the user drags or resizes a tile. */
    helperLines: {
      horizontal: helperLineHorizontal,
      vertical: helperLineVertical,
    },
    /** False until the tiles have positions to show. */
    isPlaced,
    isArranging: !layout.didFail && (layout.isPending || !isPlaced),
    didFail: layout.didFail,
    dragging,
    setDragging,
    retryLayout: layout.retry,
  };
}
